import appLogger from '@/utils/logger';
import clsx from 'clsx';
import { FC, HTMLAttributes, ReactNode } from 'react';
import Image from 'next/image';
import { FileUploader } from 'react-drag-drop-files';

interface IFileUploadProps extends HTMLAttributes<HTMLDivElement> {
  type: string[];
  files: File[];
  setFiles: (files: File[]) => void;
  customChildren?: ReactNode;
  disabled?: boolean;
  hideFileList?: boolean;
}

const FileUpload: FC<IFileUploadProps> = ({
  type,
  files,
  setFiles,
  customChildren,
  disabled = false,
  hideFileList = false,
  className,
}: IFileUploadProps) => {
  const handleChange = (files: File | File[] | FileList) => {
    try {
      let fileArray: File[] = [];
      
      if (files instanceof FileList) {
        // Convert FileList to Array
        fileArray = Array.from(files);
      } else if (Array.isArray(files)) {
        fileArray = files;
      } else if (files instanceof File) {
        fileArray = [files];
      } else {
        appLogger.error('Unexpected file type:', { data: files });
        setFiles([]);
        return;
      }
      
      // Filter out any invalid files
      const validFiles = fileArray.filter(file => file instanceof File);
      
      setFiles(validFiles);
    } catch (error) {
      appLogger.error('Error in FileUpload handleChange:', { data: error });
      setFiles([]);
    }
  };

  return (
    <>
      <FileUploader
        handleChange={handleChange}
        name='files'
        types={type}
        multiple
      >
        <div>
          {customChildren ?? (
            <div
              className={clsx(
                `flex h-[80px] min-w-[300px] px-[12px] py-[10px] justify-center items-center 
                              marker:gap-[10px] align-self-stretch rounded-md border-dashed border-2 
                            border-gray-300 bg-[#F8F9FA] shadow-md md:w-[255px] w-full cursor-pointer hover:border-gray-600 group`,
                className,
              )}
            >
              <p className='text-14 first-line:text-gray-500 font-inter text-base font-normal leading-5 group-hover:first-line:text-gray-700'>
                Upload or drag files here
              </p>
            </div>
          )}
        </div>
      </FileUploader>
      {!hideFileList && files.length > 0 && (
        <div className='flex flex-wrap gap-2 mt-4'>
          {[...files].map((file: File, key: number) => {
            // Safely access file properties
            const fileName = file?.name || 'Unknown file';
            
            return (
              <div key={key} className='flex justify-center items-center gap-[11px]'>
                <Image src='/images/solar_file-bold.png' alt="File icon" className='w-[58px] h-[58px]' width={58} height={58} />
                <div className='flex flex-col items-start gap-[5px] max-w-[190px]'>
                  <p className='text-14 text-black font-inter text-base font-normal leading-5'>
                    {fileName}
                  </p>
                  <p className='text-14 text-black/50 font-inter text-base font-normal leading-5'></p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default FileUpload;
